import { z } from "zod";

import { optionalQueryParam } from "./common.schemas";

/**
 * `inputSchema` on an HTML message template is a `ParameterSetup` document (the
 * same typed, agent-fillable, nested-capable schema used by tools/workflows).
 * It is stored as JSON and fed straight into `computeAgentInputSchema` at fill
 * time, so we validate it leniently here — the shared generator owns the strict
 * draft-2020-12 guarantees. Each entry carries at least a `handler` and `type`;
 * nested objects/arrays recurse through `properties`/`items`.
 */
export const ParameterSetupEntrySchema: z.ZodType<Record<string, unknown>> = z.lazy(() =>
  z
    .object({
      id: z.string().optional(),
      handler: z.enum(["manual", "prompt", "variable"]),
      type: z.enum(["string", "number", "boolean", "object", "array", "null"]),
      prompt: z.string().optional(),
      properties: z.record(z.string(), ParameterSetupEntrySchema).optional(),
      items: z.array(ParameterSetupEntrySchema).optional()
    })
    .passthrough()
);

export const ParameterSetupSchema = z.record(z.string(), ParameterSetupEntrySchema);

/**
 * `endsTurn` (NEX-5632): when the agent's `send_html_template_*` call for this
 * template is the last tool call of its message, the agent's turn ends there —
 * the agent waits for the user instead of writing another message under the
 * card. Off by default: the turn continues after the card, so a template used
 * as a holding card mid-turn keeps working.
 */
const EndsTurnSchema = z.boolean();

export const HtmlMessageTemplateSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullish(),
  htmlContent: z.string(),
  inputSchema: ParameterSetupSchema.nullish(),
  // Defaulted, not required, on the READ side: the console parses every
  // response, and it can ship before the backend that starts sending the key.
  endsTurn: EndsTurnSchema.default(false),
  deploymentId: z.string().uuid(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date().nullish()
});

export const HtmlMessageTemplateSummarySchema = HtmlMessageTemplateSchema.omit({
  htmlContent: true
});

// ── List ────────────────────────────────────────────────────────────────────
export const ListHtmlMessageTemplatesQuerySchema = z.object({
  search: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  deploymentId: optionalQueryParam(z.string().uuid())
});

export const ListHtmlMessageTemplatesResponseSchema = z.object({
  items: z.array(HtmlMessageTemplateSummarySchema)
});

// ── Create ──────────────────────────────────────────────────────────────────
export const CreateHtmlMessageTemplateBodySchema = z.object({
  name: z.string().trim().min(1),
  description: z.string().optional(),
  htmlContent: z.string().min(1),
  inputSchema: ParameterSetupSchema.optional(),
  endsTurn: EndsTurnSchema.optional(),
  deploymentId: z.string().uuid()
});

export const CreateHtmlMessageTemplateResponseSchema = HtmlMessageTemplateSchema;

// ── Update ──────────────────────────────────────────────────────────────────
export const UpdateHtmlMessageTemplateBodySchema = z
  .object({
    name: z.string().trim().min(1).optional(),
    description: z.string().nullable().optional(),
    htmlContent: z.string().min(1).optional(),
    inputSchema: ParameterSetupSchema.nullable().optional(),
    endsTurn: EndsTurnSchema.optional()
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: "At least one field must be provided"
  });

export const UpdateHtmlMessageTemplateResponseSchema = HtmlMessageTemplateSchema;

// ── Delete ──────────────────────────────────────────────────────────────────
export const DeleteHtmlMessageTemplateResponseSchema = z.object({
  id: z.string().uuid(),
  deleted: z.literal(true)
});

// ── Render (fill with caller-provided data) ──────────────────────────────────
export const RenderHtmlMessageTemplateBodySchema = z.object({
  data: z.record(z.string(), z.unknown()).default({})
});

export const RenderHtmlMessageTemplateResponseSchema = z.object({
  html: z.string()
});

// ── Fill (agent fills the schema via structured output, then render) ──────────
export const FillHtmlMessageTemplateBodySchema = z.object({
  /** Natural-language context/instructions the agent uses to fill the schema. */
  context: z.string().min(1),
  /** Optional model override; defaults to the platform fill model. */
  model: z.string().optional()
});

export const FillHtmlMessageTemplateResponseSchema = z.object({
  html: z.string(),
  data: z.record(z.string(), z.unknown())
});

// ── Params ────────────────────────────────────────────────────────────────────
export const HtmlMessageTemplateIdParamSchema = z.object({
  templateId: z.string().uuid()
});
